import {
  Flex,
  OutlineDangerButton,
  PrimaryButton,
  TertiaryButton,
  Tooltip,
} from '@enechain/ecloud-designsystem'

type Props = {
  /** ボタン文言 (= 呼び出し元が t() 済み。 通常入札 / ブロック入札で namespace が違う)。 */
  labels: { cancel: string; reject: string; approve: string }
  /** 申請取消 (AC-BSP-055、 申請者の自己リカバリ経路)。 省略時は描画しない。 */
  onCancel?: () => void
  /** 差戻し (AC-BSP-094)。 省略時は描画しない。 */
  onReject?: () => void
  /** 承認 (AC-BSP-094)。 省略時は描画しない。 */
  onApprove?: () => void
  /**
   * 承認 / 差戻し を非活性にする理由 (= hover tooltip の文言)。 undefined なら活性。
   * 申請取消には効かない (= 申請取消の出し分けは呼び出し元が導線ごと省略する)。
   */
  decisionDisabledReason?: string
}

/**
 * 承認状態バナー (PENDING) 右端の操作群。 通常入札 (`ApprovalStatusBanner`) と
 * ブロック入札 (`BlockApprovalStatusBanner`) で i18n key 以外が逐語一致だったので共通化した。
 *
 * 並びは左から 申請取消 (申請者) → 差戻し / 承認 (承認者) で、 主操作の 承認 を右端に置く。
 * - 申請取消: 「戻す」 系の副次操作なので TertiaryButton。 実 CANCEL RPC は確認 modal 経由で
 *   呼び出し元 (ページ) が実行する
 * - 差戻し: 「破壊的操作の前段導線」 なので OutlineDangerButton、 確定は差戻し dialog 側の
 *   DangerButton
 * - 承認: 主操作なので PrimaryButton
 *
 * ## 承認 / 差戻しの無効化は非表示ではなく disabled + tooltip
 *
 * - AC-BSP-099 (締切後) は「無効化する (AC-BSP-064 の入札作成・編集 UI 無効化と同型)」 で、
 *   AC-BSP-064 の実装は入札追加の disabled + hover tooltip (NormalBidTab)
 * - 完了済ワークフロー (AC-BSP-058) / 明細未解決 (fail-closed) も同じ器に載せ、 押せない
 *   理由を hover で分かるようにする
 * - 承認者でない人・申請者本人 (AC-BSP-098) は無効化しない
 *
 * 申請取消 / 再編集は従来どおり呼び出し元が導線ごと省略する (AC-BSP-055c の締切後は
 * spec 上 Forbidden で、 無効化ではない)。
 *
 * disabled 要素は pointer event を発火しないため span で包んで hover を拾う
 * (NormalBidTab の入札追加と同パターン)。 read-only 表示 (= 全 handler
 * 省略) では空の Flex も置かない: 親 Flex の gap ぶんだけ右端に余白が残ってしまうため。
 */
export const ApprovalDecisionActions = ({
  labels,
  onCancel,
  onReject,
  onApprove,
  decisionDisabledReason,
}: Props): React.ReactElement | null => {
  if (
    onCancel === undefined &&
    onReject === undefined &&
    onApprove === undefined
  ) {
    return null
  }
  const isDecisionDisabled = decisionDisabledReason !== undefined
  const withReason = (button: React.ReactElement): React.ReactElement =>
    decisionDisabledReason === undefined ? (
      button
    ) : (
      <Tooltip content={decisionDisabledReason} placement="top" showArrow>
        <span>{button}</span>
      </Tooltip>
    )
  return (
    <Flex flexShrink={0} gap={2}>
      {onCancel !== undefined && (
        <TertiaryButton onClick={onCancel} size="sm">
          {labels.cancel}
        </TertiaryButton>
      )}
      {onReject !== undefined &&
        withReason(
          <OutlineDangerButton
            disabled={isDecisionDisabled}
            onClick={isDecisionDisabled ? undefined : onReject}
            size="sm"
          >
            {labels.reject}
          </OutlineDangerButton>,
        )}
      {onApprove !== undefined &&
        withReason(
          <PrimaryButton
            disabled={isDecisionDisabled}
            onClick={isDecisionDisabled ? undefined : onApprove}
            size="sm"
          >
            {labels.approve}
          </PrimaryButton>,
        )}
    </Flex>
  )
}
